import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { makeKey, getCache, setCache } from "@/lib/offlineCache";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Sun, Moon, Star, Clock, BellRing, Bell, BellOff, Heart, TrendingUp, CalendarCheck, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import {
  pushSupported, subscribeUser, unsubscribeUser, getSubscription, sendTestNotification,
} from "@/lib/push";
import { toast } from "sonner";

const SIGNS = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [sign, setSign] = useState("Leo");
  const [userRasi, setUserRasi] = useState(null);
  const [horoscope, setHoroscope] = useState(null);
  const [horoscopeLoading, setHoroscopeLoading] = useState(false);
  const [panchang, setPanchang] = useState(null);
  const [hora, setHora] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [userStats, setUserStats] = useState(null);

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    const loc = { lat: 28.6139, lon: 77.2090, tz: "Asia/Kolkata" };
    const pKey = makeKey("panchang", { date: today, ...loc });
    const hKey = makeKey("hora", { date: today, ...loc });
    const uKey = makeKey("upcoming-tithis", { start_date: today, tz: loc.tz, days_ahead: 30 });

    // Render cached snapshot immediately so the dashboard stays useful offline.
    const pCached = getCache(pKey); if (pCached) setPanchang(pCached.data);
    const hCached = getCache(hKey); if (hCached) setHora(hCached.data);
    const uCached = getCache(uKey); if (uCached) setUpcoming(uCached.data);

    api.post("/astro/panchang", { date: today, ...loc })
      .then(({ data }) => { setPanchang(data); setCache(pKey, data); })
      .catch(() => { /* stay on cached */ });
    api.post("/astro/hora", { date: today, ...loc })
      .then(({ data }) => { setHora(data); setCache(hKey, data); })
      .catch(() => {});
    api.post("/astro/upcoming-tithis", { start_date: today, tz: loc.tz, days_ahead: 30 })
      .then(({ data }) => { setUpcoming(data); setCache(uKey, data); })
      .catch(() => {});

    // Auto-detect user's Chandra rashi (Vedic moon sign) and pre-load horoscope.
    if (user?.birth_dob && user?.birth_time && user?.birth_lat != null && user?.birth_tz) {
      api.get("/astro/my-rashi")
        .then(({ data }) => {
          if (data?.sign) {
            setSign(data.sign);
            setUserRasi(data);
            // Auto-fetch horoscope for their sign
            api.get(`/astro/daily-horoscope/${data.sign.toLowerCase()}`)
              .then(({ data: h }) => setHoroscope(h))
              .catch(() => {});
          }
        })
        .catch(() => { /* birth details incomplete or backend error — user can still pick manually */ });
    }

    // Check push subscription state
    if (pushSupported()) {
      getSubscription().then((s) => setPushEnabled(!!s));
    }

    // If user already subscribed, trigger sacred-day check (server only fires if today is special + not yet notified)
    api.post("/push/check-sacred-day").catch(() => { /* ignore if not subscribed */ });

    // Public seeker count for social proof
    api.get("/stats/users").then(({ data }) => setUserStats(data)).catch(() => {});
  }, [today, user?.birth_dob, user?.birth_time, user?.birth_lat, user?.birth_tz]);

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushEnabled) {
        await unsubscribeUser();
        setPushEnabled(false);
        toast.success("Push notifications disabled");
      } else {
        await subscribeUser();
        setPushEnabled(true);
        toast.success("You'll be notified on Ekadashi, Purnima, and Amavasya");
      }
    } catch (e) {
      toast.error(e.message || "Couldn't change notification settings");
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    try {
      const { data } = await sendTestNotification();
      if (data.sent > 0) {
        toast.success("Test notification sent — check your system tray");
      } else {
        toast.error("No active subscriptions found");
      }
    } catch (e) {
      toast.error("Test push failed");
    }
  };

  const fetchHoroscope = async () => {
    setHoroscopeLoading(true);
    setHoroscope(null);
    try {
      const { data } = await api.get(`/astro/daily-horoscope/${sign.toLowerCase()}`);
      setHoroscope(data);
    } finally {
      setHoroscopeLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <section className="glass rounded-2xl p-8 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-2">
            <Star className="w-3 h-3" /> {new Date().toDateString()}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-slate-50 mb-2">
            {t("dash.greeting")}, <span className="text-gold italic">{user?.name?.split(" ")[0] || "Seeker"}</span>
          </h1>
          <p className="text-slate-400 max-w-xl">{t("dash.intro")}</p>
          {userStats && userStats.total_users > 0 && (
            <div
              data-testid="dashboard-user-count"
              className="mt-5 inline-flex items-center gap-2 text-xs text-slate-400 bg-white/5 border border-white/10 rounded-full px-3 py-1.5"
            >
              <Sparkles className="w-3 h-3 text-gold" />
              <span>
                <span className="text-amber-300 font-semibold" data-testid="dashboard-user-count-value">
                  {userStats.total_users.toLocaleString()}
                </span>{" "}
                cosmic seekers worldwide
                {userStats.activated_users > 0 && (
                  <span className="text-slate-500"> · {userStats.activated_users.toLocaleString()} activated</span>
                )}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming sacred days */}
      {upcoming && ((upcoming.next_ekadashis?.length || upcoming.next_ekadashi) || (upcoming.next_purnimas?.length || upcoming.next_purnima) || (upcoming.next_amavasyas?.length || upcoming.next_amavasya)) && (
        <section className="glass rounded-2xl p-6" data-testid="upcoming-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <BellRing className="w-5 h-5 text-gold" />
              <h2 className="font-serif text-xl text-slate-50">{t("dash.upcoming")}</h2>
            </div>
            {pushSupported() && (
              <div className="flex gap-2">
                <Button
                  onClick={togglePush}
                  disabled={pushBusy}
                  data-testid="push-toggle"
                  variant="outline"
                  size="sm"
                  className={`rounded-full border-amber-500/30 hover:bg-amber-500/10 gap-2 ${
                    pushEnabled ? "text-emerald-300" : "text-amber-300"
                  }`}
                >
                  {pushEnabled ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                  {pushEnabled ? t("push.on") : t("push.enable")}
                </Button>
                {pushEnabled && (
                  <Button
                    onClick={handleTestPush}
                    data-testid="push-test-btn"
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-slate-400 hover:text-amber-300 text-xs"
                  >
                    {t("push.test")}
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(upcoming.next_ekadashis && upcoming.next_ekadashis.length > 0
              ? upcoming.next_ekadashis
              : (upcoming.next_ekadashi ? [upcoming.next_ekadashi] : [])
            ).map((ek, idx) => {
              const paksha = ek.paksha === "Shukla Paksha" ? t("tag.shukla_paksha") : t("tag.krishna_paksha");
              const variant = ek.variant === "Smarta" ? t("tag.smarta") : (ek.variant === "Vaishnava" ? t("tag.vaishnava") : null);
              const sub = variant ? `${paksha} · ${variant}` : paksha;
              return (
                <UpcomingTag
                  key={`ek-${ek.date}`}
                  testId={`next-ekadashi-${idx}`}
                  label={t("tag.ekadashi")}
                  date={ek.date}
                  sub={sub}
                />
              );
            })}
            {(upcoming.next_purnimas && upcoming.next_purnimas.length > 0
              ? upcoming.next_purnimas
              : (upcoming.next_purnima ? [upcoming.next_purnima] : [])
            ).map((pu, idx) => (
              <UpcomingTag key={`pu-${pu.date}`} testId={`next-purnima-${idx}`} label={t("tag.purnima")} date={pu.date} sub={t("tag.shukla_paksha")} />
            ))}
            {(upcoming.next_amavasyas && upcoming.next_amavasyas.length > 0
              ? upcoming.next_amavasyas
              : (upcoming.next_amavasya ? [upcoming.next_amavasya] : [])
            ).map((am, idx) => (
              <UpcomingTag key={`am-${am.date}`} testId={`next-amavasya-${idx}`} label={t("tag.amavasya")} date={am.date} sub={t("tag.krishna_paksha")} />
            ))}
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Card className="glass border-white/10 p-6 card-hover" data-testid="panchang-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-xl text-slate-50">{t("dash.panchang_today")}</h3>
            <Moon className="w-5 h-5 text-gold" />
          </div>
          {panchang ? (
            <div className="space-y-2 text-sm">
              <Row label="Tithi" value={`${panchang.tithi.name} (${panchang.tithi.paksha})`} />
              <Row label="Nakshatra" value={`${panchang.nakshatra.name} • ${panchang.nakshatra.lord}`} />
              <Row label="Yoga" value={panchang.yoga} />
              <Row label="Karana" value={panchang.karana} />
              <Row label="Vaara" value={panchang.vaara} />
              <div className="flex gap-2 mt-3 flex-wrap">
                {panchang.tithi.is_ekadashi && <Badge>{t("tag.ekadashi")}</Badge>}
                {panchang.tithi.is_purnima && <Badge>{t("tag.purnima")}</Badge>}
                {panchang.tithi.is_amavasya && <Badge>{t("tag.amavasya")}</Badge>}
                {panchang.tithi.is_chaturdashi && <Badge>{t("tag.chaturdashi")}</Badge>}
              </div>
            </div>
          ) : (
            <Skeleton />
          )}
        </Card>

        <Card className="glass border-white/10 p-6 card-hover" data-testid="hora-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-xl text-slate-50">{t("dash.hora_today")}</h3>
            <Clock className="w-5 h-5 text-gold" />
          </div>
          {hora ? (
            <div className="space-y-2 text-sm">
              <Row label={t("dash.day_lord")} value={hora.day_lord} />
              <Row label={t("dash.sunrise")} value={hora.sunrise} />
              <Row label={t("dash.sunset")} value={hora.sunset} />
              {hora.current_hora && (
                <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="text-xs text-amber-300 uppercase tracking-wider mb-1">{t("dash.current_hora")}</div>
                  <div className="text-slate-50 font-medium">{hora.current_hora.lord} Hora</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {hora.current_hora.start} – {hora.current_hora.end}
                  </div>
                  <div className="text-xs text-slate-300 mt-1">{hora.current_hora.favorable_for}</div>
                </div>
              )}
            </div>
          ) : (
            <Skeleton />
          )}
        </Card>

        <Card className="glass border-white/10 p-6 card-hover" data-testid="horoscope-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-xl text-slate-50">{t("dash.horoscope")}</h3>
            <Sun className="w-5 h-5 text-gold" />
          </div>
          {userRasi && (
            <div className="text-xs text-amber-300/90 mb-2" data-testid="auto-rasi-hint">
              {t("dash.your_rasi")}: <span className="font-medium">{userRasi.rasi_name}</span>
              {userRasi.moon_nakshatra && <span className="text-slate-400"> · {userRasi.moon_nakshatra}</span>}
            </div>
          )}
          <div className="flex gap-2 mb-3">
            <Select value={sign} onValueChange={setSign}>
              <SelectTrigger data-testid="horoscope-sign-select" className="bg-slate-900/50 border-white/10 text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10 text-slate-200">
                {SIGNS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={fetchHoroscope}
              disabled={horoscopeLoading}
              data-testid="horoscope-fetch-btn"
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full"
            >
              {horoscopeLoading ? "..." : t("dash.get")}
            </Button>
          </div>
          {horoscope ? (
            <div className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed max-h-48 overflow-auto" data-testid="horoscope-text">
              {horoscope.text}
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic">
              {userRasi ? t("dash.loading_horoscope") : t("dash.pick_sign")}
            </div>
          )}
        </Card>
      </section>

      <section>
        <h2 className="font-serif text-2xl text-slate-50 mb-4">{t("dash.quick_tools")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <QuickLink to="/numerology" icon={Sparkles} title={t("nav.numerology")} />
          <QuickLink to="/name-numerology" icon={Star} title={t("nav.name")} />
          <QuickLink to="/birth-chart" icon={Moon} title={t("nav.chart")} />
          <QuickLink to="/matching" icon={Heart} title={t("nav.matching")} />
          <QuickLink to="/muhurat" icon={CalendarCheck} title="Muhurat" />
          <QuickLink to="/vastu" icon={Compass} title="Vastu" />
          <QuickLink to="/transits" icon={TrendingUp} title="Transits" />
          <QuickLink to="/chat" icon={Sun} title={t("nav.chat")} />
        </div>
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-100 font-medium text-right">{value}</span>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30">
      {children}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="h-3 bg-white/5 rounded w-full" />
      <div className="h-3 bg-white/5 rounded w-3/4" />
      <div className="h-3 bg-white/5 rounded w-1/2" />
    </div>
  );
}

function QuickLink({ to, icon: Icon, title }) {
  return (
    <Link to={to} data-testid={`quick-link-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <Card className="glass border-white/10 p-5 card-hover group">
        <Icon className="w-6 h-6 text-gold mb-3 group-hover:rotate-12 transition" />
        <div className="text-slate-100 font-medium">{title}</div>
      </Card>
    </Link>
  );
}

function UpcomingTag({ testId, label, date, sub }) {
  const d = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d - today) / 86400000);
  const dayLabel = diffDays === 0 ? "Today" : diffDays === 1 ? "Tomorrow" : `In ${diffDays} days`;
  const dateFmt = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  return (
    <div
      data-testid={testId}
      className="p-4 rounded-xl bg-slate-900/40 border border-amber-500/15 hover:border-amber-500/40 transition card-hover"
    >
      <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">{label}</div>
      <div className="text-xl font-serif text-slate-50">{dateFmt}</div>
      <div className="text-xs text-slate-400 mt-1">{dayLabel} · {sub}</div>
    </div>
  );
}
